import { Download, Import, Link2 } from 'lucide-react'
import { toast } from 'sonner'
import type { EntityContextMenuEntry } from '@/components/features/entity-context-menu'
import { NextcloudIcon } from '@/components/icons/nextcloud-icon'
import { fetchWithAuth } from '@/lib/api'
import { OpenDestinationIcons, resolveOpenExternalIcon } from '@/lib/open-destination-icons'
import {
  isNextcloudText,
  nextcloudWebUrl,
  type NextcloudEntry,
} from './nextcloud-entry'

const ExternalIcon = resolveOpenExternalIcon(NextcloudIcon)

async function readError(response: Response, fallback: string): Promise<string> {
  try {
    const body = (await response.json()) as { error?: string }
    return body.error || fallback
  } catch {
    return fallback
  }
}

export async function downloadNextcloudFile(
  organizationId: string,
  entry: NextcloudEntry,
): Promise<void> {
  const response = await fetchWithAuth(
    `/api/nextcloud/download?organizationId=${encodeURIComponent(organizationId)}&path=${encodeURIComponent(entry.path)}`,
  )
  if (!response.ok) throw new Error(await readError(response, 'Could not download that file'))
  const blob = await response.blob()
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = entry.name
  anchor.click()
  URL.revokeObjectURL(url)
}

export function buildNextcloudContextMenu(options: {
  entry: NextcloudEntry
  onPreview: () => void
  onOpen: () => void
  onOpenExternal: () => void
  onDownload: () => void
}): EntityContextMenuEntry[] {
  const { entry } = options
  const file = entry.kind === 'file'
  const blocked = Boolean(entry.encrypted)
  return [
    {
      label: 'Preview',
      icon: OpenDestinationIcons.preview,
      disabled: entry.kind === 'folder' && !blocked,
      onSelect: options.onPreview,
    },
    {
      label: 'Open',
      icon: OpenDestinationIcons.open,
      disabled: blocked,
      onSelect: options.onOpen,
    },
    {
      label: 'Import',
      icon: <Import className="h-4 w-4" />,
      disabled: blocked || !file || !isNextcloudText(entry),
      onSelect: () => toast('Import is not available yet'),
    },
    {
      label: 'Link without copy',
      icon: <Link2 className="h-4 w-4" />,
      disabled: blocked || !file,
      onSelect: () => toast('Link without copy is not available yet'),
    },
    { separator: true },
    {
      label: 'Open in Nextcloud',
      icon: <ExternalIcon className="h-4 w-4" />,
      disabled: !file || blocked,
      onSelect: options.onOpenExternal,
    },
    {
      label: 'Download',
      icon: <Download className="h-4 w-4" />,
      disabled: !file || blocked,
      onSelect: options.onDownload,
    },
  ]
}

export function openNextcloudEntry(instanceUrl: string | null | undefined, entry: NextcloudEntry) {
  const url = nextcloudWebUrl(instanceUrl, entry.fileId)
  if (!url) {
    toast('This file has no Nextcloud link yet')
    return
  }
  window.open(url, '_blank', 'noopener,noreferrer')
}
